import { type MouseEvent, forwardRef } from 'react';

import {
  Button,
  ButtonProps,
  CircularProgress,
  DialogActions,
  DialogActionsProps,
  Tooltip,
  WithStyles,
  withStyles,
} from '@material-ui/core';

import classNames from 'classnames';

import {
  SplitButton,
  SplitButtonOptionProps,
  SplitButtonProps,
} from '../SplitButton';
import { useDialogContext } from './context';

const Cancel = (props: ButtonProps) => {
  const { onClose, isLoading } = useDialogContext();
  const { children = 'Cancel', onClick, disabled, ...rest } = props;

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    if (e.defaultPrevented) {
      return;
    }
    onClose();
  };

  return (
    <Button
      variant="outlined"
      color="default"
      onClick={handleClick}
      disabled={isLoading || disabled}
      data-track-id="button.cancel"
      data-track-action="dismiss"
      {...rest}
    >
      {children}
    </Button>
  );
};

type PrimaryProps = ButtonProps & { tooltip?: string };

const Primary = ({ tooltip, ...props }: PrimaryProps) => {
  const { isLoading } = useDialogContext();
  const { children = 'Save', disabled, ...rest } = props;

  const button = (
    <Button
      color="primary"
      variant="contained"
      data-testid="primary-button"
      disabled={isLoading || disabled}
      data-track-id="button.submit"
      data-track-action="update"
      {...(isLoading && {
        endIcon: (
          <CircularProgress data-testid="progress" size="1em" color="inherit" />
        ),
      })}
      {...rest}
    >
      {children}
    </Button>
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltip} arrow>
        <span>{button}</span>
      </Tooltip>
    );
  }

  return button;
};

const Secondary = (props: ButtonProps) => {
  const { isLoading } = useDialogContext();
  const { children = 'Create', disabled, ...rest } = props;

  return (
    <Button
      color="primary"
      variant="outlined"
      data-testid="secondary-button"
      disabled={isLoading || disabled}
      data-track-id="button.secondary"
      data-track-action="update"
      {...(isLoading && {
        endIcon: (
          <CircularProgress data-testid="progress" size="1em" color="inherit" />
        ),
      })}
      {...rest}
    >
      {children}
    </Button>
  );
};

/**
 * MultiAction component that provides multiple action options using SplitButton.
 *
 * Usage example:
 * ```tsx
 * <DialogComposite.Actions>
 *   <DialogComposite.Actions.Cancel />
 *   <DialogComposite.Actions.MultiAction
 *     id="save-actions"
 *   >
 *     <DialogComposite.Actions.MultiAction.Option
 *       onClick={handleSaveAndClose}
 *       description="Save changes and close dialog"
 *     >
 *       Save & Close
 *     </DialogComposite.Actions.MultiAction.Option>
 *     <DialogComposite.Actions.MultiAction.Option
 *       onClick={handleSaveAndCreateNew}
 *       description="Save changes and create a new item"
 *     >
 *       Save & Create New
 *     </DialogComposite.Actions.MultiAction.Option>
 *   </DialogComposite.Actions.MultiAction>
 * </DialogComposite.Actions>
 * ```
 */

const MultiActionOption = (props: SplitButtonOptionProps) => {
  const { isLoading } = useDialogContext();
  const { disabled, ...rest } = props;

  return <SplitButton.Option {...rest} disabled={isLoading || disabled} />;
};

const MultiAction = (props: SplitButtonProps) => {
  const { isLoading } = useDialogContext();
  const { disabled, ...rest } = props;

  return (
    <SplitButton
      color="primary"
      variant="contained"
      data-testid="multi-action-button"
      {...rest}
      {...(isLoading && {
        endIcon: (
          <CircularProgress data-testid="progress" size="1em" color="inherit" />
        ),
      })}
      disabled={isLoading || disabled}
    />
  );
};

MultiAction.Option = MultiActionOption;

const Submit = (props: PrimaryProps) => {
  return <Primary type="submit" {...props} />;
};

const styles = {
  root: {},
};

export type ActionsProps = Omit<DialogActionsProps, 'classes'> &
  WithStyles<typeof styles>;

const Actions = forwardRef<HTMLDivElement, ActionsProps>((props, ref) => {
  const { className, classes, ...rest } = props;

  return (
    <DialogActions
      ref={ref}
      className={classNames(classes.root, className)}
      {...rest}
    />
  );
});

Actions.displayName = 'DialogComposite.Actions';

const StyledActions = withStyles(styles)(Actions);

export default Object.assign(StyledActions, {
  Cancel,
  Submit,
  Secondary,
  Primary,
  MultiAction,
});
